import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getToken } from "@/lib/api";
import { useI18n } from "@/lib/I18nContext";

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "fm_admin_secret";
const REFRESH_MS = 30_000;

/**
 * Phase 19c/d — Admin Dashboard.
 *
 * Dual-factor auth:
 *   1. Bearer JWT (via AuthContext) of a user with `is_admin === true`.
 *   2. `X-Admin-Secret` header captured via the unlock form and held in
 *      sessionStorage so refreshes inside the same tab don't re-prompt.
 *      Cleared on Lock or on any 403 from the API.
 *
 * Non-admin users are redirected to `/app`. Anonymous users are
 * redirected to `/` (AppShell handles that upstream via ProtectedRoute).
 */
export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [secret, setSecret] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) || "";
    } catch (_) {
      return "";
    }
  });

  useEffect(() => {
    // Non-admin users are bounced back to the app immediately.
    if (user && !user.is_admin) {
      navigate("/app", { replace: true });
    }
  }, [user, navigate]);

  const onLock = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch (_) {
      /* ignore */
    }
    setSecret("");
  }, []);

  const onUnlock = useCallback((value) => {
    try {
      sessionStorage.setItem(SESSION_KEY, value);
    } catch (_) {
      /* ignore */
    }
    setSecret(value);
  }, []);

  if (!user) return null; // ProtectedRoute upstream handles this state.
  if (!user.is_admin) return null; // effect above navigates away.

  if (!secret) {
    return <AdminUnlock onUnlock={onUnlock} />;
  }

  return (
    <AdminCommandCenter
      secret={secret}
      onLock={onLock}
    />
  );
}

// ─── Unlock screen ─────────────────────────────────────────────────────
function AdminUnlock({ onUnlock }) {
  const { t } = useI18n();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    setBusy(true);
    setError("");
    try {
      const r = await axios.get(
        `${API}/api/admin/telemetry/market-funnel`,
        {
          headers: {
            Authorization: `Bearer ${getToken() || ""}`,
            "X-Admin-Secret": trimmed,
          },
          validateStatus: (s) => s < 500,
        }
      );
      if (r.status === 200) {
        onUnlock(trimmed);
      } else if (r.status === 403) {
        setError(t("admin.unlock_denied", "Access Denied. Exclusive Admin Zone."));
      } else {
        setError(t("admin.unlock_denied", "Access Denied. Exclusive Admin Zone."));
      }
    } catch (_) {
      setError(t("admin.unlock_denied", "Access Denied. Exclusive Admin Zone."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-unlock-screen"
      className="min-h-[70vh] w-full flex items-center justify-center px-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-neutral-950 p-6"
      >
        <h1
          data-testid="admin-unlock-title"
          className="font-display text-xl font-semibold text-white"
        >
          {t("admin.unlock_title", "Admin Unlock")}
        </h1>
        <p
          className="mt-2 text-sm text-neutral-400 font-body"
          data-testid="admin-unlock-subtitle"
        >
          {t(
            "admin.unlock_subtitle",
            "Enter the shared admin secret to access the telemetry dashboard."
          )}
        </p>
        <input
          data-testid="admin-unlock-input"
          type="password"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("admin.unlock_placeholder", "Admin Secret Key")}
          className="mt-5 w-full bg-black border border-white/15 rounded-lg px-3 py-2 text-sm text-white font-mono placeholder:text-neutral-600 focus:outline-none focus:border-amber-400/60"
        />
        {error ? (
          <p
            data-testid="admin-unlock-error"
            className="mt-3 text-xs text-red-400 font-body"
          >
            {error}
          </p>
        ) : null}
        <button
          data-testid="admin-unlock-submit"
          type="submit"
          disabled={busy || !value.trim()}
          className="mt-5 w-full rounded-lg bg-[#E5B83A] text-black text-sm font-semibold py-2 disabled:opacity-40 hover:bg-[#f0c752] transition-colors"
        >
          {busy ? "…" : t("admin.unlock_button", "Unlock")}
        </button>
      </form>
    </div>
  );
}

// ─── Command Center (dashboard) ────────────────────────────────────────
function AdminCommandCenter({ secret, onLock }) {
  const { t } = useI18n();
  const [data, setData] = useState({
    funnel: null,
    tokens: null,
    live: null,
    demand: null,
  });
  const [error, setError] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${getToken() || ""}`,
      "X-Admin-Secret": secret,
    }),
    [secret]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchAll = async () => {
      try {
        const [funnel, tokensR, live, demand] = await Promise.all([
          axios.get(`${API}/api/admin/telemetry/market-funnel`, { headers }),
          axios.get(`${API}/api/admin/telemetry/token-circulation`, { headers }),
          axios.get(`${API}/api/admin/telemetry/live-snapshot`, { headers }),
          axios.get(`${API}/api/admin/telemetry/brand-demand`, { headers }),
        ]);
        if (cancelled) return;
        setData({
          funnel: funnel.data,
          tokens: tokensR.data,
          live: live.data,
          demand: demand.data,
        });
        setError(null);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) {
          toast.error(
            t("admin.unlock_denied", "Access Denied. Exclusive Admin Zone.")
          );
          onLock();
        } else {
          setError("network");
        }
      }
    };

    fetchAll();
    const iv = setInterval(fetchAll, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [headers, onLock, t]);

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-[70vh] w-full text-white"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
        <div>
          <h1
            data-testid="admin-dashboard-title"
            className="font-display text-2xl font-semibold text-white"
          >
            {t("admin.dashboard_title", "FiguMatch — Command Center")}
          </h1>
          <p className="mt-1 text-xs text-neutral-500 font-body">
            {t(
              "admin.ready_for_commercialization",
              "Ready for commercialization"
            )}
          </p>
        </div>
        <button
          data-testid="admin-lock-button"
          type="button"
          onClick={onLock}
          className="rounded-md border border-white/15 px-3 py-1.5 text-xs text-neutral-300 hover:border-amber-400/60 hover:text-amber-400 transition-colors"
        >
          {t("admin.lock_button", "Lock")}
        </button>
      </header>

      {error === "network" ? (
        <p className="mt-4 text-xs text-red-400 font-body">
          Network hiccup — retrying every 30s.
        </p>
      ) : null}

      <section
        data-testid="admin-cards-grid"
        className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4"
      >
        <MarketFunnelCard data={data.funnel} t={t} />
        <TokenCirculationCard data={data.tokens} t={t} />
        <LiveSnapshotCard data={data.live} t={t} />
        <BrandDemandCard data={data.demand} t={t} />
      </section>
    </div>
  );
}

// ─── Cards ─────────────────────────────────────────────────────────────
function Card({ testid, title, children }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-white/10 bg-neutral-950 p-5"
    >
      <p className="text-[11px] uppercase tracking-widest text-neutral-500 font-body">
        {title}
      </p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function KpiValue({ testid, label, value }) {
  const v = value === null || value === undefined ? "—" : value;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-neutral-400 font-body">{label}</span>
      <span
        data-testid={testid}
        className="font-display font-bold text-2xl text-[#E5B83A]"
      >
        {v}
      </span>
    </div>
  );
}

function MarketFunnelCard({ data, t }) {
  const days = data?.daily_signups_last_30d || [];
  return (
    <Card
      testid="admin-card-market-funnel"
      title={t("admin.card.market_funnel", "Market Funnel")}
    >
      <KpiValue
        testid="admin-kpi-total-signups"
        label={t("admin.kpi.total_signups", "Total Signups")}
        value={data?.total_signups}
      />
      <div className="mt-3 grid grid-cols-3 gap-2">
        {["free", "pro", "master"].map((tier) => (
          <div
            key={tier}
            data-testid={`admin-tier-${tier}`}
            className="rounded-lg border border-white/10 bg-black/40 px-2 py-2 text-center"
          >
            <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-body">
              {tier}
            </p>
            <p className="mt-1 font-display font-semibold text-lg text-white">
              {data?.by_tier?.[tier] ?? "—"}
            </p>
          </div>
        ))}
      </div>
      <SparkLine days={days} />
    </Card>
  );
}

function SparkLine({ days }) {
  const max = useMemo(
    () => Math.max(1, ...days.map((d) => d.count || 0)),
    [days]
  );
  const w = 300;
  const h = 60;
  const step = days.length > 1 ? w / (days.length - 1) : w;
  const points = days
    .map((d, i) => {
      const x = i * step;
      const y = h - ((d.count || 0) / max) * (h - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <div
      data-testid="admin-sparkline-signups"
      className="mt-4 rounded-lg border border-white/10 bg-black/40 p-3"
    >
      <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-body">
        Daily signups (last 30d)
      </p>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className="mt-2 w-full h-16"
        role="img"
        aria-label="Daily signups sparkline"
      >
        {days.length >= 2 ? (
          <polyline
            fill="none"
            stroke="#E5B83A"
            strokeWidth="1.5"
            points={points}
          />
        ) : null}
      </svg>
    </div>
  );
}

function TokenCirculationCard({ data, t }) {
  return (
    <Card
      testid="admin-card-token-circulation"
      title={t("admin.card.token_circulation", "Token Circulation")}
    >
      <KpiValue
        testid="admin-kpi-total-tokens"
        label={t("admin.kpi.total_tokens", "FiguTokens in circulation")}
        value={data?.total_figutokens_in_circulation}
      />
      <div className="mt-3">
        <KpiValue
          testid="admin-kpi-stripe-events"
          label={t("admin.kpi.stripe_events", "Stripe events processed")}
          value={data?.stripe_events_processed}
        />
      </div>
    </Card>
  );
}

function LiveSnapshotCard({ data, t }) {
  return (
    <Card
      testid="admin-card-live-snapshot"
      title={t("admin.card.live_snapshot", "Live Snapshot")}
    >
      <KpiValue
        testid="admin-kpi-active-sessions"
        label={t("admin.kpi.active_sessions", "Active sessions (24h)")}
        value={data?.active_sessions_last_24h}
      />
      <div className="mt-3 flex items-baseline justify-between gap-3">
        <span className="text-xs text-neutral-400 font-body">
          {t("admin.kpi.pwa_installs", "PWA installations")}
        </span>
        <span
          data-testid="admin-kpi-pwa-installs"
          className="font-body text-xs text-neutral-500"
        >
          {data?.pwa_installations || "—"}
        </span>
      </div>
    </Card>
  );
}

function BrandDemandCard({ data, t }) {
  const rows = data?.top_numbers || [];
  return (
    <Card
      testid="admin-card-brand-demand"
      title={t("admin.card.brand_demand", "Brand Demand Index")}
    >
      <p className="text-xs text-neutral-400 font-body">
        {t(
          "admin.kpi.top_numbers",
          "Top-20 numbers by matched-trade volume"
        )}
      </p>
      {rows.length === 0 ? (
        <p
          data-testid="admin-brand-demand-empty"
          className="mt-3 text-xs text-neutral-600 font-body"
        >
          No matched trades yet.
        </p>
      ) : (
        <ul
          data-testid="admin-brand-demand-list"
          className="mt-3 grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1"
        >
          {rows.map((r) => (
            <li
              key={r.number}
              data-testid={`admin-brand-demand-row-${r.number}`}
              className="flex items-center justify-between text-xs bg-black/40 border border-white/10 rounded-md px-2 py-1.5"
            >
              <span className="font-mono text-neutral-300">#{r.number}</span>
              <span className="font-display font-semibold text-[#E5B83A]">
                {r.matched_trade_volume}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
